'use client';

import React, { useEffect, useRef, useState } from 'react';

export default function HeroSection() {
  const heroRef = useRef<HTMLElement>(null);
  const cursorGlowRef = useRef<HTMLDivElement>(null);
  const layer1Ref = useRef<HTMLDivElement>(null);
  const layer2Ref = useRef<HTMLDivElement>(null);
  const layer3Ref = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  /* Cursor parallax */
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = hero.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;

      if (cursorGlowRef.current) {
        cursorGlowRef.current.style.left = `${e.clientX}px`;
        cursorGlowRef.current.style.top = `${e.clientY}px`;
      }
      if (layer1Ref.current) {
        layer1Ref.current.style.transform = `translate(${x * -30}px, ${y * -20}px)`;
      }
      if (layer2Ref.current) {
        layer2Ref.current.style.transform = `translate(${x * 20}px, ${y * 15}px)`;
      }
      if (layer3Ref.current) {
        layer3Ref.current.style.transform = `translate(${x * -12}px, ${y * -8}px)`;
      }
    };

    hero.addEventListener('mousemove', handleMouseMove);
    return () => hero.removeEventListener('mousemove', handleMouseMove);
  }, []);

  /* Scroll parallax */
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (layer1Ref.current) {
        layer1Ref.current.style.transform = `translateY(${scrollY * 0.3}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleExploreClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.querySelector('#philosophy');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden hero-gradient-bg grain-overlay"
      id="framework"
    >
      {/* ── Cursor Glow ── */}
      <div
        ref={cursorGlowRef}
        className="cursor-glow"
        style={{
          background: 'radial-gradient(circle, rgba(139,105,20,0.12) 0%, transparent 65%)',
          width: 500,
          height: 500,
          position: 'fixed',
          borderRadius: '50%',
          pointerEvents: 'none',
          transform: 'translate(-50%, -50%)',
          zIndex: 0,
          willChange: 'transform',
        }}
      />

      {/* ── Background Layer 1: Large blobs (slowest) ── */}
      <div
        ref={layer1Ref}
        className="absolute inset-0 pointer-events-none"
        style={{ transition: 'transform 0.15s ease-out', willChange: 'transform' }}
      >
        <div
          className="blob-1 absolute"
          style={{
            width: '55vw',
            height: '55vw',
            maxWidth: 700,
            maxHeight: 700,
            top: '-15%',
            right: '-10%',
            background: 'radial-gradient(circle, rgba(44,74,62,0.6) 0%, rgba(28,43,37,0.2) 60%, transparent 100%)',
            filter: 'blur(60px)',
            opacity: 0.5,
          }}
        />
        <div
          className="blob-2 absolute"
          style={{
            width: '45vw',
            height: '45vw',
            maxWidth: 600,
            maxHeight: 600,
            bottom: '-20%',
            left: '-8%',
            background: 'radial-gradient(circle, rgba(139,105,20,0.35) 0%, rgba(196,149,106,0.1) 60%, transparent 100%)',
            filter: 'blur(80px)',
            opacity: 0.4,
          }}
        />
      </div>

      {/* ── Background Layer 2: Mid-ground atmospheric shapes ── */}
      <div
        ref={layer2Ref}
        className="absolute inset-0 pointer-events-none"
        style={{ transition: 'transform 0.2s ease-out', willChange: 'transform' }}
      >
        {/* Geometric Islamic-inspired ring */}
        <div
          className="absolute"
          style={{
            width: 320,
            height: 320,
            top: '15%',
            right: '8%',
            borderRadius: '50%',
            border: '1px solid rgba(139,105,20,0.15)',
            opacity: 0.6,
          }}
        />
        <div
          className="absolute"
          style={{
            width: 220,
            height: 220,
            top: 'calc(15% + 50px)',
            right: 'calc(8% + 50px)',
            borderRadius: '50%',
            border: '1px solid rgba(139,105,20,0.1)',
            opacity: 0.5,
          }}
        />
        {/* Gold accent blob */}
        <div
          style={{
            position: 'absolute',
            width: 200,
            height: 200,
            top: '30%',
            left: '5%',
            borderRadius: '60% 40% 70% 30% / 50% 60% 40% 60%',
            background: 'radial-gradient(circle, rgba(139,105,20,0.25) 0%, transparent 70%)',
            filter: 'blur(40px)',
            animation: 'floatSlow 8s ease-in-out infinite',
          }}
        />
      </div>

      {/* ── Foreground Layer 3: Floating Arabic/decorative elements ── */}
      <div
        ref={layer3Ref}
        className="absolute inset-0 pointer-events-none"
        style={{ transition: 'transform 0.25s ease-out', willChange: 'transform' }}
      >
        {/* Floating ornament top-left */}
        <div
          className="float-slow absolute"
          style={{
            top: '22%',
            left: '6%',
            opacity: 0.12,
            fontSize: 64,
            color: '#8B6914',
            fontFamily: 'serif',
            lineHeight: 1,
          }}
        >
          ✦
        </div>
        {/* Floating ornament bottom-right */}
        <div
          className="float-delayed absolute"
          style={{
            bottom: '20%',
            right: '7%',
            opacity: 0.1,
            fontSize: 48,
            color: '#C4956A',
            fontFamily: 'serif',
            lineHeight: 1,
          }}
        >
          ❋
        </div>
        {/* Subtle grid lines */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(139,105,20,0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(139,105,20,0.04) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
          }}
        />
      </div>

      {/* ── Content Layer ── */}
      <div
        className="relative z-10 max-w-[1280px] mx-auto px-6 lg:px-8 w-full"
        style={{ paddingTop: '96px', paddingBottom: '80px' }}
      >
        <div className="flex flex-col items-center text-center">

          {/* Badge */}
          <div
            className={`arabic-badge mb-8 transition-all duration-700 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '0.1s' }}
          >
            بسم الله &nbsp;·&nbsp; In the Name of Allah
          </div>

          {/* Headline */}
          <h1
            className={`font-heading text-white transition-all duration-1000 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{
              fontSize: 'clamp(3.2rem, 9vw, 7.5rem)',
              lineHeight: 1.0,
              letterSpacing: '-0.03em',
              fontWeight: 600,
              maxWidth: 900,
              transitionDelay: '0.25s',
            }}
          >
            A Garden of Caretakers.
            <br />
            <span className="text-shimmer">No Walls.</span>
          </h1>

          {/* Subheadline */}
          <p
            className={`font-body text-white/60 mt-8 max-w-2xl leading-relaxed transition-all duration-1000 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{
              fontSize: 'clamp(1rem, 2vw, 1.2rem)',
              transitionDelay: '0.45s',
            }}
          >
            An open toolkit for God-Consciousness. Linking signs in creation back to the One.
            A framework for those walking toward{' '}
            <span
              className="font-mono text-secondary-light"
              style={{ fontFamily: 'var(--font-mono)' }}
            >
              Taqwah
            </span>
            {' '}— fisabilillah.
          </p>

          {/* CTA Buttons */}
          <div
            className={`flex flex-col sm:flex-row items-center gap-4 mt-12 transition-all duration-1000 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '0.65s' }}
          >
            <a
              href="#philosophy"
              onClick={handleExploreClick}
              className="group inline-flex items-center gap-3 font-body text-sm font-semibold text-white px-8 py-4 rounded-btn transition-all duration-300"
              style={{
                background: 'rgba(139,105,20,0.9)',
                border: '1px solid rgba(139,105,20,0.6)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(179,138,42,0.95)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.9)';
              }}
            >
              Explore the Framework
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className="transition-transform duration-300 group-hover:translate-x-1"
              >
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a
              href="#components"
              className="font-body text-sm font-medium text-white/60 hover:text-white/90 transition-colors duration-200 border-b border-white/20 hover:border-white/50 pb-0.5"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#components')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              See the Four Components
            </a>
          </div>

          {/* Floating stat cards */}
          <div
            className={`flex flex-wrap justify-center gap-4 mt-16 transition-all duration-1000 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '0.85s' }}
          >
            {[
              { term: 'Haal', meaning: 'State of Being', note: 'The present spiritual condition' },
              { term: 'Ayat', meaning: 'Signs & Proofs', note: 'Pointing back to Allah' },
              { term: 'Taqwah', meaning: 'God-Consciousness', note: 'The orientation of the heart' },
            ].map((item) => (
              <div
                key={item.term}
                className="flex flex-col items-center text-center px-6 py-4 rounded-card"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(8px)',
                  minWidth: 140,
                }}
              >
                <span
                  className="font-mono text-xs tracking-widest mb-1"
                  style={{ color: '#B38A2A', fontFamily: 'var(--font-mono)' }}
                >
                  {item.term}
                </span>
                <span className="font-heading text-white text-sm font-medium">{item.meaning}</span>
                <span className="font-body text-white/40 text-xs mt-1">{item.note}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Scroll indicator ── */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-1000 ${
          loaded ? 'opacity-60' : 'opacity-0'
        }`}
        style={{ transitionDelay: '1.2s', zIndex: 10 }}
      >
        <span className="font-mono text-white/40 text-xs tracking-widest uppercase">Scroll</span>
        <div
          className="w-px h-12"
          style={{
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.4), transparent)',
            animation: 'floatSlow 2s ease-in-out infinite',
          }}
        />
      </div>
    </section>
  );
}